/**
 * Card Classification — Pure function that produces a CardClassification object
 * from a ScryfallCard. Consolidates type parsing, boolean flags, land/mana-source
 * detection, and role classification into a single pre-computed object.
 *
 * Role classification logic moved from hooks/useCardRoles.ts.
 * Type helpers delegate to canonical functions in lib/cardUtils.ts.
 */
import type { CardClassification, CardRoleId, CardRoles, ScryfallCard } from '@/types/card';

import {
  getFullOracleText,
  getFullTypeLine,
  getSubTypes,
  getSuperTypes,
  getTypeCategory,
  getTypes,
  isBasicLandCard,
  isLand,
  isManaDork,
  isManaRock,
  isUtilityLand,
} from './cardUtils';

// ============================================================================
// Constants — Role display metadata & ordering
// ============================================================================

export { type CardRoleId, type CardRoleInfo, type CardRoles } from '@/types/card';

/** Role metadata for display */
export const CARD_ROLE_INFO: Record<CardRoleId, { id: CardRoleId; label: string; shortLabel: string; color: string; bgColor: string }> = {
  ramp: {
    id: 'ramp',
    label: 'Ramp',
    shortLabel: 'Ramp',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
  },
  cardDraw: {
    id: 'cardDraw',
    label: 'Card Draw',
    shortLabel: 'Draw',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
  },
  removal: {
    id: 'removal',
    label: 'Removal',
    shortLabel: 'Remove',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
  },
  boardWipe: {
    id: 'boardWipe',
    label: 'Board Wipe',
    shortLabel: 'Wipe',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
  },
  protection: {
    id: 'protection',
    label: 'Protection',
    shortLabel: 'Protect',
    color: 'text-zinc-200',
    bgColor: 'bg-zinc-500/20',
  },
  recursion: {
    id: 'recursion',
    label: 'Recursion',
    shortLabel: 'Recur',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
  },
  manaFixing: {
    id: 'manaFixing',
    label: 'Mana Fixing',
    shortLabel: 'Fixing',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
  },
  tutor: {
    id: 'tutor',
    label: 'Tutor',
    shortLabel: 'Tutor',
    color: 'text-violet-400',
    bgColor: 'bg-violet-500/20',
  },
  evasion: {
    id: 'evasion',
    label: 'Evasion',
    shortLabel: 'Evasion',
    color: 'text-sky-400',
    bgColor: 'bg-sky-500/20',
  },
};

/** Order of roles for display */
export const CARD_ROLE_ORDER: CardRoleId[] = [
  'ramp',
  'cardDraw',
  'removal',
  'boardWipe',
  'protection',
  'recursion',
  'manaFixing',
  'tutor',
  'evasion',
];

// ============================================================================
// Card Classification Patterns (moved from hooks/useCardRoles.ts)
// ============================================================================

/** Oracle text patterns for ramp cards */
const RAMP_PATTERNS = [
  /search your library for[^.]*(?:(?:basic )?land|plains|island|swamp|mountain|forest)[^.]*onto the battlefield/i,
  /add \{[wubrgc]\}.*to your mana pool/i,
  /add one mana of any color/i,
  /add one mana of any type/i,
  /add one mana of that color/i,
  /add one mana of the chosen color/i,
  /lands you control.*mana.*they produce/i,
  /(?:put|return)[^.]*land[^.]*(?:on)?to the battlefield/i,
  /play (?:an|one|two|three|any number of) additional lands?/i,
  /adds? an additional (?:\{[wubrgc]\}|one mana)/i,
];

const RAMP_KEYWORDS = ['mana dork'];

/** Land-for-land swaps (sacrifice a land, only one land enters) — fixing, not ramp */
const LAND_SWAP_PATTERNS = [
  /sacrifice an? (?:land|plains|island|swamp|mountain|forest)\b[\s\S]*?search your library for an? (?:basic )?land card/i,
  /sacrifice an? (?:land|plains|island|swamp|mountain|forest)\b[\s\S]*?search your library for[^.]*land cards[^.]*put one onto the battlefield/i,
];

/** Oracle text patterns for creatures that tap for mana */
const MANA_DORK_PATTERNS = [/\{t\}.*add \{[wubrgc]\}/i, /\{t\}:.*add.*mana/i];

/** Oracle text patterns for mana rocks (artifacts that tap for mana) */
const MANA_ROCK_ROLE_PATTERNS = [/\{t\}:?\s*add\s+\{[wubrgc]\}/i, /\{t\}:?\s*add\s+\{[wubrgc]\}\{[wubrgc]\}/i, /\{t\}.*add.*mana/i];

/** Oracle text patterns for card draw */
const CARD_DRAW_PATTERNS = [
  /draw (?:two|three|a|\d+) cards?/i,
  /whenever.*draw a card/i,
  /you may draw/i,
  /look at the top.*put.*into your hand/i,
  /draw cards? equal to/i,
];

const CARD_DRAW_KEYWORDS = ['cycling', 'investigate'];

/** Oracle text patterns for removal */
const REMOVAL_PATTERNS = [
  /destroy (?:up to (?:one|two|three|\d+) )?target (?!land)/i,
  /exile target(?!.*from (?:your |a )?graveyard)/i,
  /(?:target|each).*creature.*gets? -(?:\d+|x)\/-(?:[1-9]\d*|x)/i,
  /deals? \d+.*damage to.*(?:target|any)/i,
  /return.*to.*(?:owner's|its owner's) hand/i,
  /counter target/i,
  /target.*player.*sacrifices?/i,
  /(?:each|all|target) (?:opponent|player)s? sacrifices?/i,
  /destroy each (?:permanent|artifact|enchantment|creature)s? chosen/i,
];

/** Oracle text patterns for board wipes */
const BOARD_WIPE_PATTERNS = [
  /destroy all (?:[\w-]+ )?(?:creatures|nonland permanents|permanents|artifacts|enchantments|planeswalkers)/i,
  /exile all (?:[\w-]+ )?(?:creatures|nonland permanents|permanents|artifacts|enchantments|planeswalkers)/i,
  /(?:each|all) creatures? gets? -(?:\d+|x)\/-(?:\d+|x)/i,
  /deals? (?:\d+|x) damage to (?:each|all) creature/i,
  /return all.*to.*(?:owner's|their owners?'s?|owners') hands?/i,
  /destroy each (?:[\w-]+ )?(?:creature|nonland permanent|permanent|artifact|enchantment|planeswalker)(?! chosen)/i,
];

/** Oracle text patterns for protection/interaction */
const PROTECTION_PATTERNS = [
  /hexproof/i,
  /shroud/i,
  /protection from/i,
  /indestructible/i,
  /can't be countered/i,
  /can't be the target/i,
  /counter target spell/i,
];

const SELF_PROTECTION_KEYWORDS = ['hexproof', 'shroud', 'indestructible', 'ward', 'protection'];

/** Oracle text patterns for recursion */
const RECURSION_PATTERNS = [
  /return.*from.*graveyard.*(?:to.*hand|(?:on)?to the battlefield)/i,
  /put.*from.*(?:a )?graveyard.*(?:on)?to the battlefield/i,
  /exile.*from.*graveyard.*(?:put|return)[^.]*(?:(?:on)?to the battlefield|(?:in)?to (?:your|its owner's|their owner's) hands?)/i,
  /whenever.*dies.*return/i,
  /gains? flashback/i,
  /play (?:lands|land cards|plains|islands|swamps|mountains|forests) from your graveyard/i,
];

const RECURSION_KEYWORDS = ['flashback', 'escape', 'unearth', 'embalm', 'eternalize'];

/** Oracle text patterns for mana fixing (any-color effects) */
const MANA_FIXING_PATTERNS = [
  /add one mana of any color/i,
  /add.*mana.*of any type/i,
  /mana.*produces.*becomes.*any color/i,
  /pay.*mana.*as though it were mana of any/i,
];

/** Fetch land pattern: search for land cards */
const FETCH_LAND_PATTERN = /search your library for a.*land/i;

/** Tutor patterns: search library for non-land cards */
const TUTOR_PATTERNS = [
  /search your library for.*\bcards?\b/i,
  /search your library for.*creature/i,
  /search your library for.*instant or sorcery/i,
  /search your library for.*artifact(?! land)/i,
  /search your library for.*enchantment/i,
  /search your library for.*planeswalker/i,
];

/** Land-only search patterns (excluded from tutor) */
const LAND_ONLY_SEARCH_PATTERNS = [
  /search your library for.*(?:basic )?land cards?\b/i,
  /search your library for.*(?:plains|island|swamp|mountain|forest)/i,
];

/** Evasion keywords - combat abilities that make creatures harder to block */
const EVASION_KEYWORDS = [
  'flying',
  'trample',
  'menace',
  'shadow',
  'skulk',
  'fear',
  'intimidate',
  'horsemanship',
  'mountainwalk',
  'islandwalk',
  'swampwalk',
  'forestwalk',
  'plainswalk',
  'landwalk',
];

/** Oracle patterns for unblockable effects */
const EVASION_TEXT_PATTERNS = [/can't be blocked/i, /is unblockable/i];

/** Basic land subtypes for has_basic_land_type */
const BASIC_LAND_SUBTYPES = ['Plains', 'Island', 'Swamp', 'Mountain', 'Forest'];

// ============================================================================
// Internal Helpers
// ============================================================================

function matchesPatterns(oracleText: string | null, patterns: RegExp[]): boolean {
  if (!oracleText) return false;
  return patterns.some((p) => p.test(oracleText));
}

function hasKeyword(keywords: string[], targetKeywords: string[]): boolean {
  const lowerKeywords = keywords.map((k) => k.toLowerCase());
  return targetKeywords.some((t) => lowerKeywords.includes(t.toLowerCase()));
}

/** Remove text where the card only protects itself: keyword-ability lines and "this spell can't be countered". */
function stripSelfProtection(oracleText: string | null, keywords: string[]): string | null {
  if (!oracleText) return oracleText;
  const ownKeywords = [...new Set([...keywords.map((k) => k.toLowerCase()), ...SELF_PROTECTION_KEYWORDS])];
  const isKeywordPart = (part: string) =>
    ownKeywords.some((k) => part === k || part.startsWith(`${k} {`) || part.startsWith(`${k} from `) || part.startsWith(`${k}—`));
  return oracleText
    .replace(/this spell can't be countered\.?/gi, '')
    .split('\n')
    .filter((line) => {
      const parts = line.trim().toLowerCase().split(/,\s*/);
      return !(parts[0] && parts.every(isKeywordPart));
    })
    .join('\n');
}

// ============================================================================
// Role Classification (moved from hooks/useCardRoles.ts)
// ============================================================================

/**
 * Classify a card into gameplay roles.
 *
 * @param typeLine - Card's type line (e.g., "Legendary Creature — Human Wizard")
 * @param oracleText - Card's oracle text
 * @param keywords - Card's keywords array
 * @param producedMana - Array of colors the card can produce (for mana fixing detection)
 * @returns Object with boolean flags for each role
 */
export function classifyCardRoles(
  typeLine: string,
  rawOracleText: string | null,
  keywords: string[],
  producedMana?: string[] | null,
): CardRoles {
  // Reminder text describes keyword mechanics generically (e.g. earthbend's "return it to the battlefield")
  const oracleText = rawOracleText?.replace(/\([^)]*\)/g, '') ?? null;
  const lowerType = typeLine.toLowerCase();
  const isCreature = lowerType.includes('creature');
  const isArtifact = lowerType.includes('artifact');
  const isLandType = lowerType.includes('land');

  // Mana Fixing Detection
  const distinctColors = producedMana?.filter((c) => c !== 'C') ?? [];
  const isMultiColorLand = isLandType && distinctColors.length >= 2;
  const isFetchLand = isLandType && FETCH_LAND_PATTERN.test(oracleText ?? '');
  const isLandSearchFixing = !isLandType && matchesPatterns(oracleText, LAND_ONLY_SEARCH_PATTERNS);
  const hasAnyColorEffect = matchesPatterns(oracleText, MANA_FIXING_PATTERNS);
  const isMultiColorManaSource = !isLandType && distinctColors.length >= 2;

  const isManaFixing = isMultiColorLand || isFetchLand || isLandSearchFixing || hasAnyColorEffect || isMultiColorManaSource;

  // Tutor Detection
  const hasSearchPattern = matchesPatterns(oracleText, TUTOR_PATTERNS);
  const isLandOnlySearch = matchesPatterns(oracleText, LAND_ONLY_SEARCH_PATTERNS);
  const isTutor = hasSearchPattern && !isLandOnlySearch;

  // Evasion Detection (creatures only with innate evasion)
  const hasEvasionKeyword = isCreature && hasKeyword(keywords, EVASION_KEYWORDS);
  const hasEvasionText = isCreature && matchesPatterns(oracleText, EVASION_TEXT_PATTERNS);
  const isEvasion = hasEvasionKeyword || hasEvasionText;

  return {
    ramp:
      (!isLandType && matchesPatterns(oracleText, RAMP_PATTERNS) && !matchesPatterns(oracleText, LAND_SWAP_PATTERNS)) ||
      hasKeyword(keywords, RAMP_KEYWORDS) ||
      (isCreature && matchesPatterns(oracleText, MANA_DORK_PATTERNS)) ||
      (isArtifact && matchesPatterns(oracleText, MANA_ROCK_ROLE_PATTERNS)),

    cardDraw: matchesPatterns(oracleText, CARD_DRAW_PATTERNS) || hasKeyword(keywords, CARD_DRAW_KEYWORDS),

    removal: matchesPatterns(oracleText, REMOVAL_PATTERNS) && !isLandType,

    boardWipe: matchesPatterns(oracleText, BOARD_WIPE_PATTERNS),

    protection: matchesPatterns(stripSelfProtection(oracleText, keywords), PROTECTION_PATTERNS),

    recursion: matchesPatterns(oracleText, RECURSION_PATTERNS) || hasKeyword(keywords, RECURSION_KEYWORDS),

    manaFixing: isManaFixing,

    tutor: isTutor,

    evasion: isEvasion,
  };
}

/**
 * Get array of active roles for a card in display order.
 */
export function getActiveRoles(roles: CardRoles): CardRoleId[] {
  return CARD_ROLE_ORDER.filter((id) => roles[id]);
}

// ============================================================================
// classifyCard — Main entry point
// ============================================================================

/**
 * Produce a complete CardClassification from a ScryfallCard.
 *
 * Pure function — calls canonical type helpers from cardUtils.ts and role
 * classification above. Designed to be cached per oracle_id.
 */
export function classifyCard(card: ScryfallCard): CardClassification {
  const typeLine = getFullTypeLine(card) ?? '';
  const oracleText = getFullOracleText(card);
  const keywords = card.keywords ?? [];
  const producedMana = card.produced_mana;

  // ── Type parsing ──
  const superTypes = getSuperTypes(typeLine);
  const mainTypes = getTypes(typeLine);
  const subTypes = getSubTypes(typeLine);
  const typeCategory = getTypeCategory(typeLine);

  // ── Display strings (capitalized) ──
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const superType = superTypes.map(capitalize).join(' ');
  const mainType = mainTypes.map(capitalize).join(' ');
  const subType = subTypes.join(' ');

  // ── Boolean flags ──
  const is_legendary = superTypes.includes('legendary');
  const is_land = isLand(typeLine);
  const is_creature = mainTypes.includes('creature');
  const is_planeswalker = mainTypes.includes('planeswalker');
  // Nontraditional cards (CR 108.2a): not included in players' decks
  const NONTRADITIONAL_MAIN_TYPES: ReadonlySet<string> = new Set([
    'token',
    'emblem',
    'dungeon',
    'plane',
    'phenomenon',
    'scheme',
    'vanguard',
    'conspiracy',
    'stickers',
    'card', // checklist cards have type_line "Card"
  ]);
  const NONTRADITIONAL_SUB_TYPES: ReadonlySet<string> = new Set(['attraction', 'contraption']);
  const NONTRADITIONAL_LAYOUTS: ReadonlySet<string> = new Set([
    'token',
    'double_faced_token',
    'emblem',
    'planar',
    'scheme',
    'vanguard',
    'art_series',
  ]);
  // Also check raw type_line for types not in CARD_MAIN_TYPES (e.g. "Stickers")
  const typeLineWords = typeLine.split(/[\s—]+/).map((w) => w.toLowerCase());
  const NONTRADITIONAL_TYPE_LINE_WORDS: ReadonlySet<string> = new Set(['stickers']);
  const is_nontraditional =
    mainTypes.some((t) => NONTRADITIONAL_MAIN_TYPES.has(t)) ||
    subTypes.some((t) => NONTRADITIONAL_SUB_TYPES.has(t.toLowerCase())) ||
    typeLineWords.some((w) => NONTRADITIONAL_TYPE_LINE_WORDS.has(w)) ||
    NONTRADITIONAL_LAYOUTS.has(card.layout);

  // ── Specific nontraditional type ──
  // Priority chain uses valid Scryfall layouts + main types / sub types.
  // Layouts not in Scryfall API: dungeon, attraction, sticker, contraption,
  // checklist, card_back, conspiracy — detected via type line only.
  const nontraditional_type: string | null = !is_nontraditional
    ? null
    : card.layout === 'art_series'
      ? 'art_series'
      : mainTypes.includes('token') || card.layout === 'token' || card.layout === 'double_faced_token'
        ? 'token'
        : mainTypes.includes('emblem') || card.layout === 'emblem'
          ? 'emblem'
          : mainTypes.includes('dungeon')
            ? 'dungeon'
            : mainTypes.includes('plane') || card.layout === 'planar'
              ? 'plane'
              : mainTypes.includes('phenomenon')
                ? 'phenomenon'
                : mainTypes.includes('scheme') || card.layout === 'scheme'
                  ? 'scheme'
                  : mainTypes.includes('vanguard') || card.layout === 'vanguard'
                    ? 'vanguard'
                    : mainTypes.includes('conspiracy')
                      ? 'conspiracy'
                      : typeLineWords.includes('stickers')
                        ? 'stickers'
                        : subTypes.some((t) => t.toLowerCase() === 'attraction')
                          ? 'attraction'
                          : subTypes.some((t) => t.toLowerCase() === 'contraption')
                            ? 'contraption'
                            : 'other';

  // ── Land classification ──
  const is_basic_land = isBasicLandCard(typeLine, card.name);
  const is_utility_land = isUtilityLand(typeLine, oracleText);
  const has_basic_land_type = BASIC_LAND_SUBTYPES.some((st) => subTypes.includes(st));

  // ── Mana source classification ──
  const is_mana_dork = isManaDork(typeLine, oracleText, keywords);
  const is_mana_rock = isManaRock(typeLine, oracleText);
  const is_mana_source = is_mana_dork || is_mana_rock || is_land;

  // ── Role classification ──
  const roles = classifyCardRoles(typeLine, oracleText, keywords, producedMana);
  const active_roles = getActiveRoles(roles);

  return {
    // Type parsing
    super_types: superTypes,
    main_types: mainTypes,
    sub_types: subTypes,
    super_type: superType,
    main_type: mainType,
    sub_type: subType,
    type_category: typeCategory,

    // Type boolean flags
    is_legendary,
    is_planeswalker,
    is_creature,
    is_nontraditional,
    nontraditional_type,

    // Land classification
    is_land,
    is_basic_land,
    is_utility_land,
    has_basic_land_type,

    // Mana source classification
    is_mana_source,
    is_mana_dork,
    is_mana_rock,

    // Role classification
    roles,
    active_roles,
  };
}
